"use client";

import React, { useState } from "react";
import { Card, CardContent } from "@repo/ui";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import { getScoreColor, getScoreLabel } from "./pami.utils";
import { HEXACO_FACTORS, MOTIVATION_DIMS } from "@repo/pami";
import type { QuestionsFinales } from "./pami.types";

// ─── ScoreBar ─────────────────────────────────────────────────────────────────

function ScoreBar({ score }: { score: number }) {
  const color = getScoreColor(score);
  const pct = (score / 10) * 100;

  return (
    <div className="mt-1 pt-2">
      <div className="flex items-center mb-6">
        <span className="text-xs font-semibold" style={{ color }}>
          {getScoreLabel(score)}
        </span>
      </div>
      <div className="relative h-2 rounded-full bg-muted overflow-visible">
        <div
          className="absolute top-0 left-0 h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: color, opacity: 0.6 }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 rounded-full border-2 border-background"
          style={{
            left: `calc(${pct}% - 7px)`,
            width: 14,
            height: 14,
            backgroundColor: color,
            boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
            zIndex: 2,
          }}
        />
        <div
          className="absolute font-bold text-xs"
          style={{ top: -20, left: `calc(${pct}% - 6px)`, color }}
        >
          {score}
        </div>
      </div>
      <div
        className="flex justify-between mt-2 text-muted-foreground"
        style={{ fontSize: 10 }}
      >
        <span>0</span>
        <span>5</span>
        <span>10</span>
      </div>
    </div>
  );
}

// ─── FacetCard ────────────────────────────────────────────────────────────────

function FacetCard({
  label,
  description,
  score,
  interpretations,
}: {
  label: string;
  description: string;
  score: number;
  interpretations: { bas: string; haut: string };
}) {
  return (
    <div className="rounded-surface border bg-muted/30 p-4">
      <p className="font-semibold text-sm">{label}</p>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
        {description}
      </p>
      <div className="mt-3 space-y-1.5">
        <div className="flex items-start gap-2 text-xs">
          <span className="shrink-0 font-semibold text-etat-erreur">Score bas :</span>
          <span className="text-muted-foreground leading-relaxed">{interpretations.bas}</span>
        </div>
        <div className="flex items-start gap-2 text-xs">
          <span className="shrink-0 font-semibold text-etat-fait">Score élevé :</span>
          <span className="text-muted-foreground leading-relaxed">{interpretations.haut}</span>
        </div>
      </div>
      <ScoreBar score={score} />
    </div>
  );
}

// ─── HexacoFactorCard ─────────────────────────────────────────────────────────

export function HexacoFactorCard({
  factor,
  scores,
}: {
  factor: (typeof HEXACO_FACTORS)[number];
  scores: Record<string, number>;
}) {
  const [open, setOpen] = useState(false);
  const avgScore = Math.round(
    factor.facets.reduce((a, f) => a + (scores[f.prefix] ?? 5), 0) /
      factor.facets.length,
  );

  return (
    <Card>
      <button
        className="w-full text-left px-6 pt-5 pb-5 flex items-center gap-4 cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <div
          className="flex items-center justify-center shrink-0 rounded-full font-semibold text-base"
          style={{
            width: 40,
            height: 40,
            backgroundColor: `${factor.color}1a`,
            color: factor.color,
          }}
        >
          {factor.key}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="font-semibold text-sm">{factor.label}</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            {factor.description}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Moyenne
            </p>
            <p
              className="text-2xl font-bold leading-none"
              style={{ color: factor.color }}
            >
              {avgScore}
            </p>
          </div>
          {open ? (
            <ChevronUp className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
      </button>
      {open && (
        <CardContent className="pt-0 pb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {factor.facets.map((facet) => (
              <FacetCard
                key={facet.prefix}
                label={facet.label}
                description={facet.description}
                score={scores[facet.prefix] ?? 5}
                interpretations={facet.interpretations}
              />
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ─── MotivationDimCard ────────────────────────────────────────────────────────

export function MotivationDimCard({
  dim,
  score,
}: {
  dim: (typeof MOTIVATION_DIMS)[number];
  score: number;
}) {
  return (
    <Card>
      <div className="px-6 pt-5 pb-2 flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{dim.label}</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            {dim.description}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Score
          </p>
          <p
            className="text-2xl font-bold leading-none"
            style={{ color: getScoreColor(score) }}
          >
            {score}
          </p>
        </div>
      </div>
      <CardContent className="pt-0 pb-6">
        <div className="mt-1 space-y-1.5">
          <div className="flex items-start gap-2 text-xs">
            <span className="shrink-0 font-semibold text-etat-erreur">Score bas :</span>
            <span className="text-muted-foreground leading-relaxed">{dim.interpretations.bas}</span>
          </div>
          <div className="flex items-start gap-2 text-xs">
            <span className="shrink-0 font-semibold text-etat-fait">Score élevé :</span>
            <span className="text-muted-foreground leading-relaxed">{dim.interpretations.haut}</span>
          </div>
        </div>
        <ScoreBar score={score} />
      </CardContent>
    </Card>
  );
}

// ─── QuestionsFinalesInfo ─────────────────────────────────────────────────────

export function QuestionsFinalesInfo({ qf }: { qf: QuestionsFinales }) {
  const items = [
    { label: "Passé dans la langue maternelle", value: qf.langueMaternelle },
    { label: "Passé dans de bonnes conditions", value: qf.bonnesConditions },
    { label: "Répondu sincèrement et honnêtement", value: qf.reponsesSinceres },
  ];

  return (
    <div className="relative group inline-flex items-center">
      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 rounded-surface border bg-popover p-3 shadow-md text-xs invisible group-hover:visible z-50 pointer-events-none">
        <p className="font-semibold mb-2 text-foreground">
          Conditions de passation
        </p>
        <ul className="space-y-1.5">
          {items.map(({ label, value }) => (
            <li key={label} className="flex items-center gap-2">
              <span
                className={
                  value ? "text-etat-fait font-bold" : "text-etat-erreur font-bold"
                }
              >
                {value ? "✓" : "✗"}
              </span>
              <span className="text-foreground">{label}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
